import React, { useEffect, useState } from 'react';
import { Bell, Check } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

/** Opt-in notifications push navigateur/téléphone pour les messages directs du coach. */
export const PushOptIn = ({ en }) => {
  const { authFetch } = useClientAuth();
  const [status, setStatus] = useState('checking'); // checking|unsupported|denied|off|on|loading

  useEffect(() => {
    const check = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
        setStatus('unsupported');
        return;
      }
      if (Notification.permission === 'denied') { setStatus('denied'); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setStatus(sub ? 'on' : 'off');
      } catch { setStatus('off'); }
    };
    check();
  }, []);

  const enable = async () => {
    setStatus('loading');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return; }
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await authFetch('/api/tracking/push/public-key');
      const { public_key } = await keyRes.json();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        });
      }
      await authFetch('/api/tracking/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      setStatus('on');
    } catch {
      setStatus('off');
    }
  };

  if (status === 'checking' || status === 'unsupported') return null;

  if (status === 'on') {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-semibold" data-testid="push-status-on">
        <Check className="w-3.5 h-3.5" /> {en ? 'Push notifications on' : 'Notifications push activées'}
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <p className="text-[11px] text-zinc-500" data-testid="push-status-denied">
        {en ? 'Notifications blocked in your browser settings.' : 'Notifications bloquées dans les réglages du navigateur.'}
      </p>
    );
  }

  return (
    <button
      onClick={enable}
      disabled={status === 'loading'}
      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-purple-300 hover:text-purple-200 border border-purple-500/30 rounded-full px-3 py-1.5 transition-colors disabled:opacity-60"
      data-testid="enable-push-button"
    >
      <Bell className="w-3.5 h-3.5" /> {status === 'loading' ? (en ? 'Enabling…' : 'Activation…') : (en ? 'Enable push alerts' : 'Activer les alertes push')}
    </button>
  );
};

export default PushOptIn;
